import { type ReactNode, useEffect, useRef } from "react";
import { CheckIcon, CloseIcon } from "./icons.tsx";

/*
 * Bulk selection's controls (docs/2026-09-30-inbox-bulk-selection-shape.md): the row checkbox and the
 * bar that takes the heading's place while rows are ticked. Quiet at rest: nothing here is drawn
 * until the rep reaches for a row's checkbox, presses X or Cmd/Ctrl+A, or chooses Select on a phone.
 */

/*
 * A row's checkbox. A native checkbox, visually hidden inside a label that draws the box, so it is
 * focusable, announced and toggled by Space like any checkbox. It is a sibling of the row's own
 * button, because a control nested in a button is neither announced nor reachable. Shift is read on
 * the press, since a click relayed from the label to its input may not carry it. The box is
 * transparent unticked, so an unread dot beneath it stays visible.
 */
export function CheckBox(props: {
  checked: boolean | "mixed";
  label: string;
  className?: string;
  tabbable: boolean;
  /** Shown but not changeable (a mailbox already in the group being filled, or not linked yet). */
  disabled?: boolean;
  onToggle: (shiftKey: boolean) => void;
}) {
  const shift = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = props.checked === "mixed";
  }, [props.checked]);
  return (
    <label
      className={`lcr-check ${props.className ?? ""}`}
      onMouseDown={(event) => {
        shift.current = event.shiftKey;
        // Shift+click would otherwise select the text between here and the last click.
        if (event.shiftKey) event.preventDefault();
      }}
    >
      <input
        ref={input}
        type="checkbox"
        className="lcr-check-input"
        checked={props.checked === true}
        aria-label={props.label}
        disabled={props.disabled === true}
        tabIndex={props.tabbable ? 0 : -1}
        onChange={() => {
          const shiftKey = shift.current;
          shift.current = false;
          props.onToggle(shiftKey);
        }}
      />
      <span className="lcr-check-box" aria-hidden="true">
        {props.checked === true && <CheckIcon size={12} />}
        {props.checked === "mixed" && <span className="lcr-check-dash" />}
      </span>
    </label>
  );
}

/**
 * The bar that takes the list heading's place while rows are ticked: the master inbox's (step 10,
 * C5b left it one caller). Its count is drawn for the eye; the list says it aloud
 * (`SelectionStatus` in `inbox-v2/list-column.tsx`), so it is hidden from screen readers here.
 */
export function BulkBar(props: {
  header: "none" | "some" | "all";
  /** The count in words, and the shortest form the narrowest list column shows instead. */
  count: { long: string; short: string };
  /** Nothing ticked yet (a phone's Select before a tap): the actions wait. */
  empty: boolean;
  onToggleAll: () => void;
  onRead: () => void;
  onUnread: () => void;
  onClear: () => void;
  /** Actions a list adds before the reading pair, drawn as `.lcr-bulk-action` buttons. */
  extra?: ReactNode;
}) {
  return (
    <div className="lcr-bulk-bar" role="toolbar" aria-label="Selected conversations">
      <CheckBox
        className="lcr-bulk-all"
        checked={props.header === "all" ? true : props.header === "some" ? "mixed" : false}
        label={
          props.header === "all"
            ? "Clear the selection of every conversation shown"
            : "Select every conversation shown"
        }
        tabbable
        onToggle={props.onToggleAll}
      />
      <span className="lcr-bulk-count" aria-hidden="true">
        <span className="lcr-bulk-long">{props.count.long}</span>
        <span className="lcr-bulk-short">{props.count.short}</span>
      </span>
      <span className="lcr-bulk-actions">
        {props.extra}
        <button
          type="button"
          className="lcr-bulk-action"
          aria-label="Mark seen"
          aria-keyshortcuts="Shift+I"
          title="Mark seen (Shift+I)"
          disabled={props.empty}
          onClick={props.onRead}
        >
          <span className="lcr-bulk-long">Mark seen</span>
          <span className="lcr-bulk-short">Seen</span>
          <kbd className="lcr-kbd">⇧I</kbd>
        </button>
        <button
          type="button"
          className="lcr-bulk-action"
          aria-label="Mark unseen"
          aria-keyshortcuts="U"
          title="Mark unseen (U)"
          disabled={props.empty}
          onClick={props.onUnread}
        >
          <span className="lcr-bulk-long">Mark unseen</span>
          <span className="lcr-bulk-short">Unseen</span>
          <kbd className="lcr-kbd">U</kbd>
        </button>
        <button
          type="button"
          className="lcr-bulk-clear"
          aria-label="Clear the selection"
          aria-keyshortcuts="Escape"
          title="Clear the selection (Esc)"
          onClick={props.onClear}
        >
          <CloseIcon size={16} />
        </button>
      </span>
    </div>
  );
}
